import React, { useId } from 'react';
import './MedSchoolPanel.css';

function formatLastChecked(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

function CitationLinks({ sourceIds, sources, className = '' }) {
  const resolved = (sourceIds || [])
    .map((id) => sources.find((item) => item.id === id))
    .filter(Boolean);
  if (!resolved.length) return null;
  return (
    <span className={`med-school-cites ${className}`.trim()}>
      {resolved.map((source) => (
        <a
          key={source.id}
          className="med-school-cite"
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${source.title} (opens in a new tab)`}
        >
          {source.title}
        </a>
      ))}
    </span>
  );
}

function SectionSources({ sourceIds, sources }) {
  const resolved = (sourceIds || [])
    .map((id) => sources.find((item) => item.id === id))
    .filter(Boolean);
  if (!resolved.length) return null;
  return (
    <p className="med-school-section-sources">
      Sources:{' '}
      {resolved.map((source, index) => (
        <React.Fragment key={source.id}>
          {index > 0 ? ' · ' : null}
          <a href={source.url} target="_blank" rel="noopener noreferrer" aria-label={`${source.title} (opens in a new tab)`}>
            {source.title}
          </a>
        </React.Fragment>
      ))}
    </p>
  );
}

function ExternalIcon() {
  return (
    <svg className="med-school-ext-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M6 3h7v7" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M13 3L6.5 9.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 3H3.5A1.5 1.5 0 002 4.5v8A1.5 1.5 0 003.5 14h8A1.5 1.5 0 0013 12.5V7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function DifficultyStars({ stars, confidence }) {
  const unrated = stars == null || confidence === 'insufficient';
  if (unrated) {
    return (
      <div className="med-school-difficulty">
        <span className="med-school-difficulty-label">Admissions difficulty</span>
        <span className="med-school-not-rated">Not rated</span>
      </div>
    );
  }
  return (
    <div className="med-school-difficulty">
      <span className="med-school-difficulty-label">Admissions difficulty</span>
      <span className="med-school-stars" aria-label={`Admissions difficulty: ${stars} out of 5`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <svg
            key={n}
            className={`med-school-star ${n <= stars ? 'is-filled' : ''}`}
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M12 3.6l2.35 4.76 5.25.76-3.8 3.7.9 5.22L12 15.9l-4.7 2.14.9-5.22-3.8-3.7 5.25-.76L12 3.6z" />
          </svg>
        ))}
      </span>
    </div>
  );
}

function FactCard({ fact, unpublishedFallback = false }) {
  const value = fact?.value || (unpublishedFallback ? 'Not published — check the official source' : null);
  if (!value && !fact?.note) return null;
  return (
    <div className="med-school-fact">
      <div className="med-school-fact-label">{fact.label}</div>
      {value ? <div className="med-school-fact-value">{value}</div> : null}
      {fact.note ? <p className="med-school-fact-note">{fact.note}</p> : null}
      <CitationLinks sourceIds={fact.sourceIds} sources={fact._sources} />
    </div>
  );
}

function withSources(fact, sources) {
  if (!fact) return null;
  return { ...fact, _sources: sources };
}

function hasSection(section) {
  return Boolean(section && (section.summary || (section.bullets && section.bullets.length)));
}

function EditorialBlock({ title, section, sources }) {
  if (!hasSection(section)) return null;
  return (
    <div className="med-school-editorial">
      <h3>{title}</h3>
      {section.summary ? <p>{section.summary}</p> : null}
      {section.bullets?.length ? (
        <ul>
          {section.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      <SectionSources sourceIds={section.sourceIds} sources={sources} />
    </div>
  );
}

export default function MedSchoolPanel({ school, open, onToggle, headerRef }) {
  const reactId = useId();
  const panelId = `med-school-panel-${school.id}-${reactId}`;
  const locationParts = [school.city, school.region].filter(Boolean);
  const locationText = locationParts.length ? locationParts.join(' · ') : school.locationLabel;
  const sources = school.sources || [];
  const req = school.requirements;
  const extraFacts = [
    ...(req.alternatives || []),
    req.englishLanguage,
    req.graduateApplicants,
    req.resits,
    req.deferredEntry,
  ].filter((fact) => fact && (fact.value || fact.note));

  const competitiveText = school.competitiveProfile?.text || null;
  const competitiveCaveat = school.competitiveProfile?.caveat || null;
  const hasCompetitive = Boolean(competitiveText || competitiveCaveat);

  const hasSelection = school.selectionSteps.length > 0 || school.selectionProse;
  const hasInterviewPair = hasSection(school.interview) || hasSection(school.contextual);
  const hasCoursePair = hasSection(school.courseAndTeaching) || hasSection(school.placements) || hasSection(school.studentExperience);
  const hasPlacePair = hasSection(school.geographyAndCity) || hasSection(school.money);
  const lastChecked = formatLastChecked(school.lastVerified);

  return (
    <article className={`med-school-panel ${open ? 'is-open' : ''}`}>
      <button
        ref={headerRef}
        type="button"
        className="med-school-header"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="med-school-header-main">
          <span className="med-school-name">{school.name}</span>
          <span className="med-school-location">
            <svg className="med-school-pin" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M12 21s7-6.2 7-11.2A7 7 0 005 9.8C5 14.8 12 21 12 21z" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <circle cx="12" cy="9.8" r="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            {locationText}
          </span>
        </span>
        <DifficultyStars stars={school.difficulty.stars} confidence={school.difficulty.confidence} />
        <svg className="med-school-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      {open ? (
        <div className="med-school-body" id={panelId}>
          <div className="med-school-overview">
            <div className="med-school-overview-top">
              <p className="med-school-course">
                {[school.courseName, school.courseCode].filter(Boolean).join(' · ') || null}
              </p>
            </div>
            {school.summary ? <p className="med-school-summary">{school.summary}</p> : null}
          </div>

          <section className="med-school-section" aria-labelledby={`${panelId}-req`}>
            <h3 id={`${panelId}-req`}>Requirements</h3>
            <div className="med-school-facts">
              <FactCard fact={withSources(req.aLevel, sources)} unpublishedFallback />
              <FactCard fact={withSources(req.gcse, sources)} unpublishedFallback />
              <FactCard fact={withSources(req.admissionsTest, sources)} unpublishedFallback />
              <FactCard fact={withSources(req.sjt, sources)} unpublishedFallback />
            </div>
            {school.checkQualificationDetails ? (
              <p className="med-school-check-note">Check qualification details</p>
            ) : null}
            {school.ucatManualNote ? (
              <p className="med-school-check-note">{school.ucatManualNote}</p>
            ) : null}
            {extraFacts.length ? (
              <div className="med-school-extra-facts">
                {extraFacts.map((fact) => (
                  <FactCard key={fact.label} fact={withSources(fact, sources)} />
                ))}
              </div>
            ) : null}
          </section>

          {hasCompetitive ? (
            <section className="med-school-section" aria-labelledby={`${panelId}-comp`}>
              <h3 id={`${panelId}-comp`} className="med-school-sr-only">Competitive profile</h3>
              <div className="med-school-competitive">
                <h4>A competitive application has historically included</h4>
                {competitiveText ? <p>{competitiveText}</p> : null}
                {competitiveCaveat ? <p className="med-school-caveat">{competitiveCaveat}</p> : null}
                {school.competitiveProfile?.evidenceCycle ? (
                  <p className="med-school-cycle">Evidence cycle: {school.competitiveProfile.evidenceCycle}</p>
                ) : null}
                <SectionSources sourceIds={school.competitiveProfile?.sourceIds} sources={sources} />
              </div>
            </section>
          ) : null}

          {hasSelection ? (
            <section className="med-school-section" aria-labelledby={`${panelId}-sel`}>
              <h3 id={`${panelId}-sel`}>How applications are selected</h3>
              {school.selectionSteps.length ? (
                <ol className="med-school-steps">
                  {school.selectionSteps.map((step, index) => (
                    <li key={`${step.title}-${index}`}>
                      <span className="med-school-step-num">{String(index + 1).padStart(2, '0')}</span>
                      <span className="med-school-step-title">{step.title}</span>
                      {step.detail ? <p>{step.detail}</p> : null}
                      <SectionSources sourceIds={step.sourceIds} sources={sources} />
                    </li>
                  ))}
                </ol>
              ) : (
                <p>{school.selectionProse}</p>
              )}
            </section>
          ) : null}

          {hasInterviewPair ? (
            <section className="med-school-section" aria-labelledby={`${panelId}-int`}>
              <h3 id={`${panelId}-int`} className="med-school-sr-only">Interview and contextual admissions</h3>
              <div className="med-school-pair">
                <EditorialBlock title="Interview" section={school.interview} sources={sources} />
                <EditorialBlock title="Contextual and widening participation" section={school.contextual} sources={sources} />
              </div>
            </section>
          ) : null}

          {hasCoursePair ? (
            <section className="med-school-section" aria-labelledby={`${panelId}-course`}>
              <h3 id={`${panelId}-course`} className="med-school-sr-only">Course and student experience</h3>
              <div className="med-school-pair">
                <div>
                  <EditorialBlock title="Course and teaching" section={school.courseAndTeaching} sources={sources} />
                  <EditorialBlock title="Placements" section={school.placements} sources={sources} />
                </div>
                <EditorialBlock title="Student experience" section={school.studentExperience} sources={sources} />
              </div>
            </section>
          ) : null}

          {hasPlacePair ? (
            <section className="med-school-section" aria-labelledby={`${panelId}-place`}>
              <h3 id={`${panelId}-place`} className="med-school-sr-only">Place and practicalities</h3>
              <div className="med-school-pair">
                <EditorialBlock title="Geography and city" section={school.geographyAndCity} sources={sources} />
                <EditorialBlock title="Money" section={school.money} sources={sources} />
              </div>
            </section>
          ) : null}

          <section className="med-school-section" aria-labelledby={`${panelId}-src`}>
            <h3 id={`${panelId}-src`}>Sources</h3>
            <ul className="med-school-sources">
              {sources.map((source) => (
                <li key={source.id}>
                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                    <span>{source.title}</span>
                    <ExternalIcon />
                    <span className="med-school-sr-only"> (opens in a new tab)</span>
                  </a>
                </li>
              ))}
            </ul>
            {lastChecked ? <p className="med-school-checked">Last checked {lastChecked}</p> : null}
            {school.filters.ucatMode === 'not_published' ? (
              <p className="med-school-data-note">The university has not yet published a numeric threshold for this cycle.</p>
            ) : null}
          </section>
        </div>
      ) : (
        <div id={panelId} hidden />
      )}
    </article>
  );
}

export { formatLastChecked };
